import type {
  GetActiveSessionResponse,
  GetSessionSnapshotResponse,
} from "@nlc/shared-types";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { uploadingSessionKeys } from "@/hooks/queryKeys";

import type { FileResult } from "../types";

import {
  discardSession,
  getActiveSession,
  getSessionSnapshot,
} from "../api/confidence-checker.api";
import {
  isSnapshotTerminal,
  snapshotToFileResults,
} from "../utils/mapEvaluation";

const POLLING_INTERVAL_MS = 15_000;

interface UseUploadingSessionQueriesOptions {
  isOpen: boolean;
  localFilesRef: MutableRefObject<Map<number, File>>;
  requestId: number;
}

interface UseUploadingSessionQueriesReturn {
  activeSessionQuery: ReturnType<typeof useQuery<GetActiveSessionResponse>>;
  claimSessionId: () => string;
  hasResolvedResume: boolean;
  invalidateSnapshot: () => void;
  invalidateSnapshotFor: (targetSessionId: string) => void;
  results: FileResult[];
  resumeSession: () => Promise<void>;
  sessionId: null | string;
  setHasResolvedResume: Dispatch<SetStateAction<boolean>>;
  setSessionId: (value: null | string) => void;
  snapshot: GetSessionSnapshotResponse | undefined;
  snapshotQuery: ReturnType<typeof useQuery<GetSessionSnapshotResponse>>;
  startFresh: () => Promise<void>;
  syncSnapshot: (
    nextSnapshot: GetSessionSnapshotResponse,
    targetSessionId: string,
  ) => void;
}

export function useUploadingSessionQueries({
  isOpen,
  localFilesRef,
  requestId,
}: UseUploadingSessionQueriesOptions): UseUploadingSessionQueriesReturn {
  const queryClient = useQueryClient();
  const [sessionId, setSessionIdState] = useState<null | string>(null);
  const [hasResolvedResume, setHasResolvedResume] = useState(false);
  const sessionIdRef = useRef<null | string>(null);

  const setSessionId = useCallback((value: null | string) => {
    sessionIdRef.current = value;
    setSessionIdState(value);
  }, []);

  const claimSessionId = useCallback(() => {
    sessionIdRef.current ??= crypto.randomUUID();

    return sessionIdRef.current;
  }, []);

  const activeSessionQuery = useQuery<GetActiveSessionResponse>({
    enabled: isOpen && requestId > 0,
    queryFn: () => getActiveSession(requestId),
    queryKey: uploadingSessionKeys.active(requestId),
    staleTime: 0,
  });

  const snapshotQuery = useQuery<GetSessionSnapshotResponse>({
    enabled: isOpen && sessionId !== null,
    queryFn: () => getSessionSnapshot(sessionId as string),
    queryKey: uploadingSessionKeys.snapshot(sessionId),
    refetchInterval: (query) =>
      isSnapshotTerminal(query.state.data) ? false : POLLING_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  const invalidateSnapshotFor = useCallback(
    (targetSessionId: string) => {
      void queryClient.invalidateQueries({
        queryKey: uploadingSessionKeys.snapshot(targetSessionId),
      });
    },
    [queryClient],
  );

  const invalidateSnapshot = useCallback(() => {
    if (!sessionId) return;
    invalidateSnapshotFor(sessionId);
  }, [invalidateSnapshotFor, sessionId]);

  const syncSnapshot = useCallback(
    (nextSnapshot: GetSessionSnapshotResponse, targetSessionId: string) => {
      queryClient.setQueryData(
        uploadingSessionKeys.snapshot(targetSessionId),
        nextSnapshot,
      );
    },
    [queryClient],
  );

  const results = useMemo<FileResult[]>(() => {
    if (!snapshotQuery.data) return [];

    return snapshotToFileResults(snapshotQuery.data, localFilesRef.current);
  }, [localFilesRef, snapshotQuery.data]);

  const resumeSession = useCallback(async () => {
    const existing = activeSessionQuery.data?.session;

    if (!existing) return;
    setSessionId(existing.id);
    setHasResolvedResume(true);
  }, [activeSessionQuery.data?.session, setSessionId]);

  const startFresh = useCallback(async () => {
    const existing = activeSessionQuery.data?.session;

    if (existing) {
      try {
        await discardSession(existing.id);
      } catch {
        /* see comment in discardAndClose */
      }
      void queryClient.invalidateQueries({
        queryKey: uploadingSessionKeys.active(requestId),
      });
    }
    setHasResolvedResume(true);
  }, [activeSessionQuery.data?.session, queryClient, requestId]);

  return {
    activeSessionQuery,
    claimSessionId,
    hasResolvedResume,
    invalidateSnapshot,
    invalidateSnapshotFor,
    results,
    resumeSession,
    sessionId,
    setHasResolvedResume,
    setSessionId,
    snapshot: snapshotQuery.data,
    snapshotQuery,
    startFresh,
    syncSnapshot,
  };
}
